import React from 'react';
import Head from 'next/head';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext } from 'next';
import { useI18nContext } from '@procore/core-react';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import businessTypes from '../../data/filters/business-type';
import routes from '../../helpers/routes';
import StatePageContent from '../../components/Layout/StatePageContent';
import { canonicalUrl } from '../../helpers/canonical-url';
import { FilterUrlKey } from '../../helpers/filters';
import { useStyles } from './[businessType].styles';
import { usePageView } from '../../components/Analytics/utilities';
import { SeoListType } from '../../types/seo-list-type';

const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { businessType } = context.query;
  const iterableBusinessTypes = Object.values(businessTypes);
  const businessData = iterableBusinessTypes.find(
    (bt) => bt.urlKey === businessType
  );
  if (businessData) {
    return { props: { businessType: businessData } };
  }

  return {
    notFound: true,
  };
};

export { getServerSideProps };

export default function BusinessTypePage({
  businessType,
}: {
  businessType: SeoListType;
}) {
  const I18n = useI18nContext();
  const classes = useStyles();
  const router = useRouter();
  const canonicalURL = canonicalUrl(router.asPath);
  usePageView(`View Company Type - ${businessType.name}`);

  return (
    <>
      <Head>
        <title>
          {I18n.t('pages.business-types.find', { name: businessType.name })}
        </title>
        <meta
          name="description"
          content={`Find and connect with thousands of ${businessType.name} in the Commercial Construction industry on Procore. Connect with construction pros building on Procore.`}
        />
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <Box className={classes.root}>
        <main>
          <Header />
          <StatePageContent
            hero={{
              title: I18n.t('pages.business-types.hero.title', {
                name: businessType.name,
              }),
              subTitle: I18n.t('pages.business-types.hero.sub-title', {
                name: businessType.name,
              }),
            }}
            item={businessType}
            itemType={FilterUrlKey.companyType}
            getStateLink={(type, country, state) =>
              routes.businessTypes(type.urlKey, country, state)
            }
          />
        </main>
        <Footer />
      </Box>
    </>
  );
}
